/**
 * Conversion between a UTC ISO-8601 instant and the `YYYY-MM-DDTHH:mm` wall-clock
 * string an `<input type="datetime-local">` expects — performed *through a given
 * IANA timezone* rather than the browser's local zone.
 *
 * A `datetime-local` input has no timezone: it renders and returns bare wall-clock
 * digits and the browser interprets them as its own local zone on `new Date(value)`.
 * Slicing a UTC ISO string straight into the input (`iso.slice(0, 16)`) and
 * reparsing on submit therefore silently shifts the instant by the browser's UTC
 * offset on every round-trip for any non-UTC user — even when the user only edited
 * an unrelated field. These helpers do the real conversion so the digits the user
 * sees and edits are the wall-clock time in *their* configured timezone
 * (UserPreferencesContext), while the stored value stays a stable UTC instant.
 */

import { zonedParts } from './timezone';

/**
 * The wall-clock of `date` in `timeZone`, re-encoded as a pseudo-UTC millisecond
 * value (i.e. `Date.UTC(...)` of its zoned Y/M/D h:m:s). Used both to derive the
 * zone offset and to check whether a candidate instant reproduces a target wall
 * clock.
 */
function zonedWallMs(date: Date, timeZone: string): number {
  const p = zonedParts(date, timeZone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

/** Offset (ms) of `timeZone` from UTC at instant `date`, where local = utc + offset. */
function tzOffsetMs(date: Date, timeZone: string): number {
  return zonedWallMs(date, timeZone) - date.getTime();
}

const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * UTC ISO instant → `YYYY-MM-DDTHH:mm` wall-clock in `timeZone`, ready to drop into
 * a `datetime-local` input's value. Returns '' for null/empty/invalid input.
 */
export function toDatetimeLocalValue(iso: string | null | undefined, timeZone: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const p = zonedParts(date, timeZone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/**
 * `YYYY-MM-DDTHH:mm` wall-clock in `timeZone` → UTC ISO instant.
 * Returns null for empty/malformed input.
 *
 * DST edges (the target wall clock is `wallMs` re-encoded as pseudo-UTC):
 *  - Normal / post-transition times: the candidate that actually reproduces the
 *    typed wall clock is chosen (the naive single-offset guess is wrong for the
 *    hour right after spring-forward, so we verify).
 *  - Fall-back overlap (a wall clock that occurs twice): resolves to the FIRST
 *    (earlier, pre-transition) occurrence — deterministic; minute-precision input
 *    can't express which the user meant.
 *  - Spring-forward gap (a wall clock that never occurs): neither offset
 *    reproduces it, so we apply the forward convention (the later instant), i.e.
 *    02:30 on a spring-forward day resolves to 03:30 — matching Temporal/date-fns.
 */
export function fromDatetimeLocalValue(
  local: string | null | undefined,
  timeZone: string,
): string | null {
  if (!local) return null;
  const match = local.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;
  const [, year, month, day, hour, minute, second] = match;
  // The typed wall clock, re-encoded as pseudo-UTC ms.
  const wallMs = Date.UTC(+year, +month - 1, +day, +hour, +minute, second ? +second : 0);

  // Candidate 1: subtract the zone offset sampled at the pseudo-UTC instant.
  const offset1 = tzOffsetMs(new Date(wallMs), timeZone);
  const candidate1 = wallMs - offset1;
  if (zonedWallMs(new Date(candidate1), timeZone) === wallMs) {
    return new Date(candidate1).toISOString();
  }
  // Candidate 2: re-sample the offset at candidate1 (corrects the post-DST edge).
  const offset2 = tzOffsetMs(new Date(candidate1), timeZone);
  const candidate2 = wallMs - offset2;
  if (zonedWallMs(new Date(candidate2), timeZone) === wallMs) {
    return new Date(candidate2).toISOString();
  }
  // Gap: the wall clock does not exist — take the later instant (forward shift).
  return new Date(Math.max(candidate1, candidate2)).toISOString();
}

/**
 * Wall-clock parts in `timeZone` → UTC ISO instant. "9am tomorrow, where the user is."
 *
 * The same conversion `fromDatetimeLocalValue` performs, addressed by PARTS instead of by
 * an input's string — for the callers that COMPUTE a wall clock rather than read one out
 * of a `datetime-local` field (a reminder preset, a scheduled-send default). Those callers
 * are deliberately outside the `no-restricted-imports` ban on the two round-trip helpers:
 * that ban exists to stop a hand-rolled datetime EDITOR bypassing `LocalizedDateTimeInput`,
 * and a computed instant is not an editor. Keeping this here rather than letting each
 * caller re-derive it is what keeps the DST handling above in exactly one module.
 */
export function wallClockToIso(
  parts: { year: number; month: number; day: number; hour: number; minute?: number },
  timeZone: string,
): string | null {
  const { year, month, day, hour, minute = 0 } = parts;
  return fromDatetimeLocalValue(
    `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`,
    timeZone,
  );
}
